import React from 'react';
import type { Headline } from '../../../lib/campaign-analytics/overview';
import { headlineCaptions } from '../../../lib/campaign-analytics/caption';
import { formatMinutes, formatPercent } from '../../../lib/campaign-analytics/format';
import { TONE_CLASSES, toneAt } from './chartTheme';
import { Skeleton } from '../../ui/Skeleton';
import { CollectionPresence } from '../../ui/CollectionPresence';

/** Props for {@link HeadlineStrip}. */
export interface HeadlineStripProps {
  /** Omit while the answers needed for these values are loading. */
  headline?: Headline;
}

/** Three big, friendly numbers, each in its own tone with one plain caption. */
export const HeadlineStrip: React.FC<HeadlineStripProps> = ({ headline }) => {
  const captions = headline && headlineCaptions(headline);
  const tiles = [
    { label: 'Responses', value: headline && String(headline.responses), caption: captions?.responses },
    { label: 'Finished', value: headline && (headline.completionRate === null ? '' : formatPercent(headline.completionRate)), caption: captions?.completed },
    { label: 'Typical time', value: headline && formatMinutes(headline.medianMinutes), caption: captions?.time },
  ];
  return (
    <dl data-testid="headline-strip" className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {tiles.filter(tile => !headline || tile.value).map((tile, index) => (
        <div key={tile.label} className={`${TONE_CLASSES[toneAt(index)]} flex flex-col gap-1 rounded-[20px] px-6 py-5`}>
          <dt className="text-sm text-muted-foreground">{tile.label}</dt>
          <dd data-testid="headline-value" className="text-3xl font-semibold tabular-nums text-foreground">
            <CollectionPresence contentKey={headline ? 'ready' : 'loading'}>{tile.value ?? <Skeleton width="4ch" inline />}</CollectionPresence>
          </dd>
          {(!headline || tile.caption) && <dd data-testid="headline-caption" className="text-sm text-muted-foreground">
            <CollectionPresence contentKey={headline ? 'ready' : 'loading'}>{tile.caption ?? <Skeleton width="12ch" inline />}</CollectionPresence>
          </dd>}
        </div>
      ))}
    </dl>
  );
};
